import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Users, Trash2, Check, X, Ban, RotateCcw, KeyRound } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { EmptyState, StatusBadge } from "../../components/Common";
import { ADMIN_MENU } from "../../lib/menus";
import { formatDate } from "../../lib/format";
import api from "../../lib/api";

export default function AdminMembers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [resetMember, setResetMember] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const load = useCallback(() => {
    setData(null);
    const sp = new URLSearchParams();
    if (q) sp.set("q", q);
    if (status) sp.set("status", status);
    sp.set("page", page);
    api.get(`/admin/members?${sp.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], pages: 0 }));
  }, [q, status, page]);

  useEffect(() => { document.title = "Kelola Member - DS KODE"; load(); }, [load]);

  const setStatusFor = async (id, s) => {
    try { await api.put(`/admin/members/${id}/status`, { status: s }); toast.success(`Status diubah menjadi ${s}`); load(); } catch { toast.error("Gagal"); }
  };
  const del = async (id) => {
    if (!window.confirm("Hapus member ini?")) return;
    try { await api.delete(`/admin/members/${id}`); toast.success("Member dihapus"); load(); } catch { toast.error("Gagal"); }
  };
  const resetPassword = async (event) => {
    event.preventDefault();
    try {
      await api.put(`/admin/members/${resetMember.id}/password`, { new_password: newPassword });
      toast.success("Password member berhasil direset");
      setResetMember(null);
      setNewPassword("");
    } catch (error) { toast.error(error?.response?.data?.detail || "Gagal mereset password"); }
  };

  return (
    <DashboardLayout title="Kelola Member" menu={ADMIN_MENU} testid="admin-members">
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Cari nama, email, username..." className="input pl-9" data-testid="member-search" /></div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="input sm:w-48" data-testid="member-filter-status"><option value="">Semua Status</option><option value="PENDING">Pending</option><option value="APPROVED">Approved</option><option value="REJECTED">Rejected</option><option value="SUSPENDED">Suspended</option></select>
      </div>

      {data === null ? <div className="h-64 skeleton rounded-xl" /> : data.items.length === 0 ? <EmptyState icon={Users} title="Belum ada member." /> : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead className="bg-slate-50 border-b border-slate-200"><tr><th className="th">Nama</th><th className="th">Email</th><th className="th">WhatsApp</th><th className="th">Download</th><th className="th">Status</th><th className="th text-right">Aksi</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((m) => (
                  <tr key={m.id} data-testid={`member-row-${m.id}`}>
                    <td className="td"><p className="font-medium text-slate-900">{m.name}</p><p className="text-xs text-slate-400">@{m.username}</p></td>
                    <td className="td text-slate-500">{m.email}</td>
                    <td className="td text-slate-500">{m.whatsapp || "-"}</td>
                    <td className="td">{m.download_count}</td>
                    <td className="td"><StatusBadge status={m.status} /></td>
                    <td className="td">
                      <div className="flex items-center justify-end gap-1">
                        {m.status !== "APPROVED" && <IconBtn title="Setujui" onClick={() => setStatusFor(m.id, "APPROVED")} icon={Check} cls="text-emerald-600 hover:bg-emerald-50" testid={`approve-${m.id}`} />}
                        {m.status !== "REJECTED" && <IconBtn title="Tolak" onClick={() => setStatusFor(m.id, "REJECTED")} icon={X} cls="text-red-600 hover:bg-red-50" testid={`reject-${m.id}`} />}
                        {m.status !== "SUSPENDED" && <IconBtn title="Nonaktifkan" onClick={() => setStatusFor(m.id, "SUSPENDED")} icon={Ban} cls="text-amber-600 hover:bg-amber-50" testid={`suspend-${m.id}`} />}
                        {m.status === "SUSPENDED" && <IconBtn title="Aktifkan" onClick={() => setStatusFor(m.id, "APPROVED")} icon={RotateCcw} cls="text-sky-600 hover:bg-sky-50" />}
                        <IconBtn title="Reset password" onClick={() => { setResetMember(m); setNewPassword(""); }} icon={KeyRound} cls="text-indigo-600 hover:bg-indigo-50" testid={`reset-password-${m.id}`} />
                        <IconBtn title="Hapus" onClick={() => del(m.id)} icon={Trash2} cls="text-slate-400 hover:bg-slate-100" testid={`delete-member-${m.id}`} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && data.pages > 1 && (
        <div className="flex justify-center gap-2 mt-6">{Array.from({ length: data.pages }).map((_, i) => <button key={i} onClick={() => setPage(i + 1)} className={`h-9 w-9 rounded-lg text-sm font-semibold ${page === i + 1 ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{i + 1}</button>)}</div>
      )}
      {resetMember && (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-900/50 p-4" onClick={() => setResetMember(null)}>
          <form onSubmit={resetPassword} onClick={(event) => event.stopPropagation()} className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6">
            <div><h3 className="font-heading font-bold text-slate-900">Reset Password Member</h3><p className="mt-1 text-sm text-slate-500">{resetMember.email}</p></div>
            <input required minLength={8} type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} className="input" placeholder="Password baru minimal 8 karakter" autoFocus />
            <div className="flex gap-2"><button type="button" onClick={() => setResetMember(null)} className="btn-outline flex-1">Batal</button><button type="submit" className="btn-primary flex-1">Simpan Password</button></div>
          </form>
        </div>
      )}
    </DashboardLayout>
  );
}

function IconBtn({ title, onClick, icon: Icon, cls, testid }) {
  return <button title={title} onClick={onClick} data-testid={testid} className={`p-2 rounded-lg ${cls}`}><Icon className="h-4 w-4" /></button>;
}
